import { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

interface JsonViewerProps {
  data: Record<string, any>;
  className?: string;
  defaultExpanded?: boolean;
  forceExpandAll?: boolean;
}

interface JsonNodeProps {
  keyName: string;
  value: any;
  depth: number;
  defaultExpanded?: boolean;
  forceExpandAll?: boolean;
}

const JsonNode = ({ keyName, value, depth, defaultExpanded = false, forceExpandAll }: JsonNodeProps) => {
  const [isManualExpanded, setIsManualExpanded] = useState(defaultExpanded && depth < 2);

  // Use forceExpandAll if defined, otherwise use manual state
  const isExpanded = forceExpandAll !== undefined ? forceExpandAll : isManualExpanded;

  const isObject = value !== null && typeof value === "object";
  const isArray = Array.isArray(value);
  const isEmpty = isObject && Object.keys(value).length === 0;

  const toggleExpand = () => {
    if (isObject && !isEmpty && forceExpandAll === undefined) {
      setIsManualExpanded(!isManualExpanded);
    }
  };

  const renderValue = () => {
    if (value === null) return <span className="text-muted-foreground">null</span>;
    if (value === undefined) return <span className="text-muted-foreground">undefined</span>;
    if (typeof value === "boolean") return <span className="text-primary">{value.toString()}</span>;
    if (typeof value === "number") return <span className="text-blue-500">{value}</span>;
    if (typeof value === "string") {
      // Check if it looks like a URL or path
      if (value.length > 50) {
        return <span className="text-green-600 dark:text-green-400 break-all">"{value}"</span>;
      }
      return <span className="text-green-600 dark:text-green-400">"{value}"</span>;
    }
    return null;
  };

  if (!isObject || isEmpty) {
    return (
      <div className="flex items-start gap-1 py-0.5" style={{ paddingLeft: `${depth * 16}px` }}>
        <span className="text-muted-foreground font-medium">{keyName}:</span>
        {isEmpty ? (
          <span className="text-muted-foreground">{isArray ? "[]" : "{}"}</span>
        ) : (
          renderValue()
        )}
      </div>
    );
  }

  const entries = Object.entries(value);
  const bracketOpen = isArray ? "[" : "{";
  const bracketClose = isArray ? "]" : "}";

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 py-0.5 cursor-pointer hover:bg-muted/50 rounded",
          isExpanded && "font-medium"
        )}
        style={{ paddingLeft: `${depth * 16}px` }}
        onClick={toggleExpand}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        )}
        <span className="text-muted-foreground">{keyName}:</span>
        {!isExpanded && (
          <span className="text-muted-foreground text-xs ml-1">
            {bracketOpen}...{bracketClose} ({entries.length} {entries.length === 1 ? "item" : "items"})
          </span>
        )}
      </div>

      {isExpanded && (
        <div>
          {entries.map(([key, val]) => (
            <JsonNode
              key={key}
              keyName={isArray ? `[${key}]` : key}
              value={val}
              depth={depth + 1}
              defaultExpanded={defaultExpanded}
              forceExpandAll={forceExpandAll}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const JsonViewer = ({ data, className, defaultExpanded = false, forceExpandAll }: JsonViewerProps) => {
  return (
    <div className={cn("font-mono text-xs bg-muted/30 rounded-lg p-3 overflow-x-auto", className)}>
      {Object.entries(data).map(([key, value]) => (
        <JsonNode
          key={key}
          keyName={key}
          value={value}
          depth={0}
          defaultExpanded={defaultExpanded}
          forceExpandAll={forceExpandAll}
        />
      ))}
    </div>
  );
};
